// Configuración (§ 3.6): paneles de auditoría, papelera, pipelines, workflows,
// formularios web e importaciones. Lecturas + acciones básicas (restaurar/vaciar).
import type { JSX } from "react";
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api.ts";
import { usePipelines } from "../lib/lookups.ts";
import type { Row } from "../lib/resource.ts";
import { formatDateTime } from "../lib/format.ts";
import { useAllCustomFields, type CustomFieldRow } from "../lib/customFields.ts";
import {
  Ban, Boxes, Download, Eye, EyeOff, FileText, GitBranch, Globe, HardDriveDownload, KeyRound, type LucideIcon, Mail, Pencil, Plus, Save, Send,
  ShieldAlert, ShieldCheck, Tag, Trash2, Upload, UserCheck, UserX, Zap,
} from "lucide-react";
import { useMe } from "../lib/auth.ts";
import { Badge, Button, Card, DotBadge, EmptyState, Field, Input, Loading, Modal, Select, Textarea } from "../components/ui.tsx";
import { SearchInput } from "../components/SearchInput.tsx";
import { useBusinessUnits } from "../components/BusinessUnitSelect.tsx";
import { CountryFlag } from "../components/CountryFlag.tsx";
import { matchesQuery } from "../lib/textMatch.ts";
import { toast } from "../components/toast.tsx";
import { ApisPanel } from "./settings/ApisPanel.tsx";

type Panel = "customfields" | "businessunits" | "email" | "portfolio" | "audit" | "trash" | "pipelines" | "workflows" | "webforms" | "imports" | "seguridad" | "backup" | "apis";
const PANELS: Array<{ key: Panel; label: string; icon: LucideIcon }> = [
  { key: "customfields", label: "Personalizados", icon: Tag },
  { key: "businessunits", label: "Unidades de negocio", icon: Boxes },
  { key: "email", label: "Servidor de correo", icon: Mail },
  { key: "portfolio", label: "Portafolio", icon: FileText },
  { key: "seguridad", label: "Seguridad · IPs", icon: ShieldAlert },
  { key: "backup", label: "Backup / Despliegue", icon: HardDriveDownload },
  { key: "audit", label: "Auditoría", icon: ShieldCheck },
  { key: "trash", label: "Papelera", icon: Trash2 },
  { key: "pipelines", label: "Pipelines", icon: GitBranch },
  { key: "workflows", label: "Workflows", icon: Zap },
  { key: "webforms", label: "Formularios web", icon: Globe },
  { key: "imports", label: "Importaciones", icon: Download },
  { key: "apis", label: "APIs", icon: KeyRound },
];

export function Settings(): JSX.Element {
  const [panel, setPanel] = useState<Panel>("customfields");
  return (
    <div className="space-y-4">
      <h1 className="page-title">Configuración</h1>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-1 p-2 h-fit">
          {PANELS.map((p) => {
            const Icon = p.icon;
            return (
              <button key={p.key} onClick={(e) => { setPanel(p.key); e.currentTarget.blur(); }}
                className={`group w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${panel === p.key ? "bg-primary-soft text-primary font-medium" : "text-ink-secondary hover:bg-surface-hover hover:text-ink"}`}>
                <Icon size={18} strokeWidth={1.75} className="shrink-0 transition-transform duration-150 ease-out-expo group-hover:scale-[1.08]" />{p.label}
              </button>
            );
          })}
        </Card>
        <div className="lg:col-span-3 min-w-0">
          {panel === "customfields" && <CustomFieldsPanel />}
          {panel === "businessunits" && <BusinessUnitsPanel />}
          {panel === "email" && <EmailSettingsPanel />}
          {panel === "portfolio" && <PortfolioPanel />}
          {panel === "seguridad" && <SecurityIpsPanel />}
          {panel === "backup" && <BackupPanel />}
          {panel === "audit" && <AuditPanel />}
          {panel === "trash" && <TrashPanel />}
          {panel === "pipelines" && <PipelinesPanel />}
          {panel === "workflows" && <WorkflowsPanel />}
          {panel === "webforms" && <WebformsPanel />}
          {panel === "imports" && <ImportsPanel />}
          {panel === "apis" && <ApisPanel />}
        </div>
      </div>
    </div>
  );
}

interface EmailCfg { tenantId: string; clientId: string; fromEmail: string; hasSecret: boolean; secretMask: string }

function EmailSettingsPanel(): JSX.Element {
  const cfg = useQuery<EmailCfg>({ queryKey: ["email-settings"], queryFn: () => api.get("/settings/email") });
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [testTo, setTestTo] = useState("");

  // Carga inicial: rellena el formulario cuando llega la config.
  if (cfg.data && !loaded) {
    setTenantId(cfg.data.tenantId);
    setClientId(cfg.data.clientId);
    setFromEmail(cfg.data.fromEmail);
    setClientSecret(cfg.data.hasSecret ? cfg.data.secretMask : "");
    setTestTo((prev) => prev || cfg.data!.fromEmail);
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: () => api.put("/settings/email", { tenantId, clientId, clientSecret, fromEmail }),
    onSuccess: () => toast.success("Configuración guardada correctamente."),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });
  const test = useMutation({
    mutationFn: () => api.post("/settings/email/test", { toEmail: testTo }),
    onSuccess: () => toast.success(`Correo de prueba enviado a ${testTo}.`),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo enviar el correo de prueba."),
  });

  if (cfg.isLoading) return <Loading />;

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink">
          <Mail size={18} strokeWidth={1.75} className="text-primary" /> Servidor de correo (Microsoft Graph)
        </h3>
        <p className="text-sm text-ink-secondary mt-1 mb-5">
          Envío vía Microsoft Graph API (Microsoft 365 bloquea SMTP básico). Requiere una App Registration en Azure AD
          con permiso <Badge>Mail.Send</Badge> de aplicación.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Tenant ID"><Input value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" /></Field>
          <Field label="Client ID"><Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="00000000-0000-0000-0000-000000000000" /></Field>
          <Field label="Client Secret">
            <div className="relative">
              <Input
                type={showSecret ? "text" : "password"}
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Valor del secreto de Azure AD"
              />
              <button type="button" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink-secondary" onClick={() => setShowSecret((v) => !v)} aria-label="Mostrar/ocultar">
                {showSecret ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
              </button>
            </div>
            <p className="text-xs text-ink-muted mt-1">Si ya está guardado se muestra como {cfg.data?.secretMask}; déjalo así para no sobrescribirlo.</p>
          </Field>
          <Field label="Correo remitente"><Input type="email" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@tudominio.com" /></Field>
        </div>
        <div className="mt-5">
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            <Save size={16} strokeWidth={1.75} /> {save.isPending ? "Guardando…" : "Guardar configuración"}
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-base font-semibold text-ink mb-3">Probar conexión</h3>
        <div className="flex items-center gap-3">
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="correo@destino.com" className="flex-1" />
          <Button variant="primary" onClick={() => test.mutate()} disabled={test.isPending || !testTo}>
            <Send size={16} strokeWidth={1.75} /> {test.isPending ? "Enviando…" : "Probar conexión"}
          </Button>
        </div>
        <p className="text-xs text-ink-muted mt-2">Guarda la configuración antes de probar. Se enviará un correo de prueba con el remitente configurado.</p>
      </Card>
    </div>
  );
}

interface BUnit { id: string; name: string; description: string | null; color: string; sortOrder: number; isActive: boolean }

/** Texto legible (blanco/negro) según la luminancia del color de fondo. */
function readableText(hex: string): string {
  const m = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!m) return "#ffffff";
  const n = parseInt(m[1]!, 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  // luminancia relativa aproximada
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#111827" : "#ffffff";
}

function SecurityIpsPanel(): JSX.Element {
  const qc = useQueryClient();
  const me = useMe();
  const q = useQuery<{ available: boolean; blocked: Array<{ ip: string; scenario: string; duration: string; origin: string }>; whitelist: string[]; error?: string }>({
    queryKey: ["security-ips"], queryFn: () => api.get("/settings/security/ips"),
  });
  const [ip, setIp] = useState(""); const [dur, setDur] = useState("4h"); const [reason, setReason] = useState("");
  const [wlIp, setWlIp] = useState("");
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["security-ips"] }); };
  const block = useMutation({ mutationFn: () => api.post("/settings/security/block", { ip, duration: dur, ...(reason ? { reason } : {}) }), onSuccess: () => { toast.success("IP bloqueada en todo el servidor."); setIp(""); setReason(""); inval(); }, onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo bloquear.") });
  const unblock = useMutation({ mutationFn: (x: string) => api.post("/settings/security/unblock", { ip: x }), onSuccess: () => { toast.success("IP desbloqueada."); inval(); }, onError: () => toast.error("No se pudo desbloquear.") });
  const wlAdd = useMutation({ mutationFn: () => api.post("/settings/security/whitelist", { ip: wlIp }), onSuccess: () => { toast.success("IP permitida (whitelist)."); setWlIp(""); inval(); }, onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo agregar.") });
  const wlDel = useMutation({ mutationFn: (x: string) => api.post("/settings/security/whitelist/remove", { ip: x }), onSuccess: () => { toast.success("Quitada de whitelist."); inval(); }, onError: () => toast.error("No se pudo quitar.") });

  if (!me.data?.admin) return <Card className="p-6"><p className="text-sm text-ink-muted">Solo administradores.</p></Card>;
  if (q.isLoading || !q.data) return <Loading />;
  const d = q.data;

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink"><ShieldAlert size={18} strokeWidth={1.75} className="text-primary" /> IPs bloqueadas / permitidas — todo el servidor</h3>
        <p className="text-xs text-ink-muted mt-1">Estas acciones afectan a <b>todo el servidor</b> (Tiger, jaque y SSH) mediante CrowdSec, sin importar desde qué aplicación las realices.</p>
        {!d.available && (
          <div className="mt-4 p-3 rounded-md border border-warning/40 bg-warning-soft">
            <p className="text-sm font-medium text-ink">Falta habilitar el control de CrowdSec (una sola vez, con <code>sudo</code>):</p>
            <pre className="text-[11px] mt-2 overflow-x-auto bg-surface p-2 rounded border border-border">{`sudo cp /home/k3/security/csctl.sh /usr/local/sbin/csctl
sudo chown root:root /usr/local/sbin/csctl && sudo chmod 755 /usr/local/sbin/csctl
echo 'k3 ALL=(root) NOPASSWD: /usr/local/sbin/csctl' | sudo tee /etc/sudoers.d/csctl
sudo chmod 440 /etc/sudoers.d/csctl && sudo visudo -c`}</pre>
          </div>
        )}
      </Card>

      {d.available && (
        <>
          <Card className="p-6">
            <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2"><Ban size={16} strokeWidth={1.75} className="text-danger" /> Bloquear una IP</h3>
            <div className="flex flex-wrap items-end gap-2">
              <Field label="IP o rango"><Input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="203.0.113.5" className="w-44" /></Field>
              <Field label="Duración"><Input value={dur} onChange={(e) => setDur(e.target.value)} placeholder="4h" className="w-20" /></Field>
              <Field label="Motivo"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="opcional" className="w-40" /></Field>
              <Button variant="danger" onClick={() => block.mutate()} disabled={!ip || block.isPending}><Ban size={15} strokeWidth={1.75} /> Bloquear</Button>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-base font-semibold text-ink mb-3">IPs bloqueadas ({d.blocked.length})</h3>
            {d.blocked.length === 0 ? <p className="text-sm text-ink-muted">Ninguna IP bloqueada en este momento.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-ink-muted text-xs"><th className="py-1">IP</th><th>Motivo / escenario</th><th>Duración</th><th></th></tr></thead>
                <tbody>{d.blocked.map((b, i) => (
                  <tr key={b.ip + i} className="border-t border-border">
                    <td className="py-1.5 font-mono">{b.ip}</td><td className="text-ink-secondary">{b.scenario}</td><td>{b.duration}</td>
                    <td className="text-right"><button className="btn-ghost btn-sm text-primary" onClick={() => unblock.mutate(b.ip)}>Desbloquear</button></td>
                  </tr>))}</tbody>
              </table>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="text-base font-semibold text-ink mb-3 flex items-center gap-2"><ShieldCheck size={16} strokeWidth={1.75} className="text-success" /> IPs permitidas (whitelist — nunca se bloquean)</h3>
            <div className="flex items-end gap-2 mb-3">
              <Field label="IP"><Input value={wlIp} onChange={(e) => setWlIp(e.target.value)} placeholder="192.0.2.10" className="w-44" /></Field>
              <Button onClick={() => wlAdd.mutate()} disabled={!wlIp || wlAdd.isPending}><Plus size={15} strokeWidth={1.75} /> Permitir</Button>
            </div>
            {d.whitelist.length === 0 ? <p className="text-sm text-ink-muted">Sin IPs en whitelist.</p> : (
              <ul className="divide-y divide-border">{d.whitelist.map((w) => (
                <li key={w} className="flex items-center justify-between py-1.5"><span className="font-mono text-sm">{w}</span>
                  <button className="btn-ghost btn-sm text-danger" onClick={() => wlDel.mutate(w)}>Quitar</button></li>))}</ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function BackupPanel(): JSX.Element {
  const me = useMe();
  const [downloading, setDownloading] = useState(false);
  if (!me.data?.admin) return <Card className="p-6"><p className="text-sm text-ink-muted">Solo administradores.</p></Card>;
  const download = (): void => { setDownloading(true); window.location.href = "/api/v1/settings/backup"; setTimeout(() => setDownloading(false), 5000); };
  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink"><HardDriveDownload size={18} strokeWidth={1.75} className="text-primary" /> Backup y despliegue</h3>
        <p className="text-sm text-ink-secondary mt-2">Descarga un paquete con <b>la base de datos actual</b>, <b>todo el código fuente</b> y una <b>guía de despliegue</b> para instalar Tiger en otro servidor.</p>
        <ul className="text-sm text-ink-secondary mt-3 list-disc pl-5 space-y-1">
          <li><b>database.sql.gz</b> — dump completo de PostgreSQL en este momento.</li>
          <li><b>source.tar.gz</b> — código fuente completo (sin node_modules).</li>
          <li><b>.env.example</b> + <b>DEPLOY.md</b> — variables necesarias y pasos de instalación.</li>
        </ul>
        <div className="mt-4"><Button onClick={download} disabled={downloading}><Download size={16} strokeWidth={1.75} /> {downloading ? "Generando…" : "Descargar backup + despliegue"}</Button></div>
        <p className="text-xs text-ink-muted mt-2">Por seguridad, los <b>secretos reales no se incluyen</b>; define nuevos en el servidor destino (ver DEPLOY.md). El paquete puede pesar decenas de MB y tardar unos segundos.</p>
      </Card>
    </div>
  );
}

function BusinessUnitsPanel(): JSX.Element {
  const qc = useQueryClient();
  const isAdmin = Boolean(useMe().data?.admin);
  const list = useQuery<{ rows: BUnit[] }>({ queryKey: ["business-units"], queryFn: () => api.get("/business-units") });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<BUnit | null>(null);

  const invalidate = (): void => { void qc.invalidateQueries({ queryKey: ["business-units"] }); };
  const del = (u: BUnit): void => {
    if (!window.confirm(`¿Eliminar la unidad de negocio "${u.name}"?`)) return;
    api.del(`/business-units/${u.id}`)
      .then(() => { toast.success("Unidad eliminada."); invalidate(); })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : "No se pudo eliminar."));
  };

  if (list.isLoading) return <Loading />;
  const rows = list.data?.rows ?? [];

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="flex items-center gap-2 text-base font-semibold text-ink"><Boxes size={18} strokeWidth={1.75} className="text-primary" /> Unidades de negocio</h3>
          <p className="text-sm text-ink-secondary mt-1">Líneas de negocio de la empresa. Cada una con su nombre, descripción y color.</p>
        </div>
        {isAdmin && <Button variant="primary" onClick={() => setCreating(true)}>+ Nueva unidad</Button>}
      </div>

      {rows.length === 0 ? (
        <EmptyState title="Aún no hay unidades de negocio" {...(isAdmin ? { body: "Crea la primera con “+ Nueva unidad”." } : {})} />
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((u) => (
            <li key={u.id} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold" style={{ background: u.color, color: readableText(u.color) }}>
                    {u.name}
                  </span>
                  {!u.isActive && <span className="badge bg-surface-hover text-ink-muted">Inactiva</span>}
                  <span className="text-[11px] text-ink-muted font-mono uppercase">{u.color}</span>
                </div>
                {u.description && <div className="text-xs text-ink-muted mt-1.5 truncate">{u.description}</div>}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-3 shrink-0">
                  <button className="text-xs text-primary hover:underline" onClick={() => setEditing(u)}>Editar</button>
                  <button className="text-xs text-ink-muted hover:text-danger" onClick={() => del(u)}>Eliminar</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {(creating || editing) && (
        <BusinessUnitForm
          unit={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); invalidate(); }}
        />
      )}
    </Card>
  );
}

function BusinessUnitForm({ unit, onClose, onSaved }: { unit: BUnit | null; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [name, setName] = useState(unit?.name ?? "");
  const [description, setDescription] = useState(unit?.description ?? "");
  const [color, setColor] = useState(unit?.color ?? "#21386D");
  const [err, setErr] = useState("");
  const save = useMutation({
    mutationFn: () => unit
      ? api.patch(`/business-units/${unit.id}`, { name, description, color })
      : api.post("/business-units", { name, description, color }),
    onSuccess: () => { toast.success(unit ? "Unidad actualizada." : "Unidad creada."); onSaved(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });
  const submit = (): void => { if (!name.trim()) { setErr("El nombre es obligatorio."); return; } setErr(""); save.mutate(); };
  const validHex = /^#[0-9a-fA-F]{6}$/.test(color);
  return (
    <Modal open onClose={onClose} title={unit ? "Editar unidad de negocio" : "Nueva unidad de negocio"}
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? "Guardando…" : "Guardar"}</Button></>}>
      <div className="space-y-4">
        <Field label="Nombre" required {...(err ? { error: err } : {})}><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Safety" /></Field>
        <Field label="Descripción"><Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Breve descripción de la unidad…" /></Field>
        <Field label="Color">
          <div className="flex items-center gap-3">
            <input type="color" value={validHex ? color : "#21386D"} onChange={(e) => setColor(e.target.value)} className="h-10 w-14 rounded-md cursor-pointer border border-line bg-transparent p-0.5" aria-label="Selector de color" />
            <Input value={color} onChange={(e) => setColor(e.target.value)} placeholder="#21386D" className="w-40 font-mono uppercase" />
            <span className="w-9 h-9 rounded-lg ring-1 ring-line/60 shrink-0" style={{ background: validHex ? color : "transparent" }} />
          </div>
          <p className="text-xs text-ink-muted mt-1">Formato #RRGGBB. Usa el selector o escribe el código.</p>
        </Field>
      </div>
    </Modal>
  );
}

interface PortItem { id: string; country: string; businessUnitId: string | null; unitName: string | null; filename: string; sizeBytes: number; uploadedAt: string }
const COUNTRY_LABEL: Record<string, string> = { CO: "Colombia", PE: "Perú", GENERAL: "General (todos)" };
const fmtSize = (b?: number): string => !b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

function PortfolioPanel(): JSX.Element {
  const qc = useQueryClient();
  const bu = useBusinessUnits();
  const list = useQuery<{ canManage: boolean; items: PortItem[] }>({ queryKey: ["portfolio-list"], queryFn: () => api.get("/settings/portfolio/list") });
  const [country, setCountry] = useState("GENERAL");
  const [unitId, setUnitId] = useState("");   // "" = general (todas las unidades)
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["portfolio-list"] }); void qc.invalidateQueries({ queryKey: ["portfolio-meta"] }); };

  const onFile = (file: File): void => {
    setUploading(true);
    const r = new FileReader();
    r.onload = () => {
      const base64 = String(r.result).split(",")[1] ?? "";
      api.post("/settings/portfolio", { country, businessUnitId: unitId || null, filename: file.name, contentType: file.type || "application/pdf", dataBase64: base64 })
        .then(() => { toast.success("Portafolio guardado."); inval(); })
        .catch((e) => toast.error(e instanceof ApiError ? e.message : "No se pudo subir el portafolio."))
        .finally(() => { setUploading(false); if (inputRef.current) inputRef.current.value = ""; });
    };
    r.onerror = () => { setUploading(false); toast.error("No se pudo leer el archivo."); };
    r.readAsDataURL(file);
  };
  const del = useMutation({ mutationFn: (id: string) => api.del(`/settings/portfolio/${id}`), onSuccess: () => { toast.success("Portafolio eliminado."); inval(); }, onError: () => toast.error("No se pudo eliminar.") });

  if (list.isLoading || !list.data) return <Loading />;
  const { canManage, items } = list.data;

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink"><FileText size={18} strokeWidth={1.75} className="text-primary" /> Portafolios por país y unidad</h3>
        <p className="text-sm text-ink-secondary mt-1">Sube un PDF por cada <b>país</b> y <b>unidad de negocio</b>. En <b>Servicios</b> se muestra el que corresponde a la selección; en los <b>correos</b> se adjunta el de la unidad/país de la cotización, con respaldo al <b>General</b>.</p>
      </Card>

      {canManage && (
        <Card className="p-6">
          <h3 className="text-base font-semibold text-ink mb-3">Subir / reemplazar</h3>
          <input ref={inputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
          <div className="flex flex-wrap items-end gap-3">
            <Field label="País"><Select value={country} onChange={(e) => setCountry(e.target.value)} className="w-44">
              <option value="GENERAL">General (todos)</option><option value="CO">Colombia</option><option value="PE">Perú</option>
            </Select></Field>
            <Field label="Unidad de negocio"><Select value={unitId} onChange={(e) => setUnitId(e.target.value)} className="w-56">
              <option value="">General (todas)</option>
              {(bu.data?.rows ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </Select></Field>
            <Button variant="primary" onClick={() => inputRef.current?.click()} disabled={uploading}><Upload size={16} strokeWidth={1.75} /> {uploading ? "Subiendo…" : "Subir PDF"}</Button>
          </div>
          <p className="text-xs text-ink-muted mt-2">Reemplaza el PDF existente para esa combinación país + unidad. Solo PDF.</p>
        </Card>
      )}

      <Card className="p-6">
        <h3 className="text-base font-semibold text-ink mb-3">Portafolios cargados ({items.length})</h3>
        {items.length === 0 ? <p className="text-sm text-ink-muted">Aún no hay portafolios cargados.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-ink-muted text-xs"><th className="py-1">País</th><th>Unidad</th><th>Archivo</th><th>Tamaño</th><th></th></tr></thead>
            <tbody>{items.map((it) => (
              <tr key={it.id} className="border-t border-border">
                <td className="py-1.5">{COUNTRY_LABEL[it.country] ?? it.country}</td>
                <td>{it.unitName || "General (todas)"}</td>
                <td className="text-ink-secondary truncate max-w-[220px]">{it.filename}</td>
                <td>{fmtSize(it.sizeBytes)}</td>
                <td className="text-right whitespace-nowrap">
                  <a className="btn-ghost btn-sm" href={`/api/v1/settings/portfolio?country=${it.country}&businessUnitId=${it.businessUnitId ?? ""}`} target="_blank" rel="noreferrer" title="Ver"><Eye size={15} strokeWidth={1.75} /></a>
                  {canManage && <button className="btn-ghost btn-sm text-danger" onClick={() => del.mutate(it.id)} disabled={del.isPending} title="Eliminar"><Trash2 size={15} strokeWidth={1.75} /></button>}
                </td>
              </tr>))}</tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function AuditPanel(): JSX.Element {
  const audit = useQuery<{ rows: Row[] }>({ queryKey: ["audit"], queryFn: () => api.get("/audit") });
  if (audit.isLoading) return <Loading />;
  const rows = audit.data?.rows ?? [];
  return (
    <Card>
      {rows.length === 0 ? <EmptyState title="Sin registros de auditoría" /> : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-canvas"><th className="th">Fecha</th><th className="th">Acción</th><th className="th">Módulo</th><th className="th">Registro</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={String(r["id"])}>
                  <td className="td">{formatDateTime(r["createdAt"] as string)}</td>
                  <td className="td"><Badge>{String(r["action"])}</Badge></td>
                  <td className="td">{String(r["module"])}</td>
                  <td className="td font-mono text-xs text-ink-muted">{String(r["recordId"] ?? "").slice(0, 8)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

const TRASH_MODULES = ["leads", "contacts", "accounts", "deals", "products", "quotes"];

function TrashPanel(): JSX.Element {
  const qc = useQueryClient();
  const [module, setModule] = useState("leads");
  const trash = useQuery<{ rows: Row[] }>({ queryKey: ["trash", module], queryFn: () => api.get(`/trash/${module}`) });
  const restore = useMutation({
    mutationFn: (id: string) => api.post(`/trash/${module}/${id}/restore`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["trash", module] }); toast.success("Registro restaurado."); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo restaurar."),
  });
  const empty = useMutation({
    mutationFn: () => api.post(`/trash/${module}/empty`),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ["trash", module] }); toast.success(`Papelera vaciada (${(r as { deleted?: number }).deleted ?? 0}).`); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo vaciar."),
  });
  const rows = trash.data?.rows ?? [];
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <Select value={module} onChange={(e) => setModule(e.target.value)} className="w-48">
          {TRASH_MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
        </Select>
        {rows.length > 0 && <Button variant="danger" small onClick={() => { if (window.confirm("¿Vaciar la papelera de este módulo? Es irreversible.")) empty.mutate(); }}>Vaciar papelera</Button>}
      </div>
      {trash.isLoading ? <Loading /> : rows.length === 0 ? <EmptyState title="Papelera vacía" /> : (
        <table className="w-full">
          <thead><tr className="bg-canvas"><th className="th">Registro</th><th className="th">Eliminado</th><th className="th"></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r["id"])}>
                <td className="td">{String(r["accountName"] ?? r["dealName"] ?? r["productName"] ?? r["subject"] ?? `${String(r["firstName"] ?? "")} ${String(r["lastName"] ?? "")}`).trim() || String(r["id"]).slice(0, 8)}</td>
                <td className="td">{formatDateTime(r["deletedAt"] as string)}</td>
                <td className="td text-right"><Button small onClick={() => restore.mutate(String(r["id"]))}>Restaurar</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function PipelinesPanel(): JSX.Element {
  const { data, isLoading } = usePipelines();
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-4">
      {(data?.pipelines ?? []).map((p) => (
        <Card key={p.id} className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-semibold text-ink">{p.name}</h3>
            {p.isDefault && <Badge color="blue">Por defecto</Badge>}
          </div>
          <div className="flex flex-wrap gap-2">
            {p.stages.map((s) => <DotBadge key={s.id} label={`${s.name} · ${s.probability}%`} hex={s.color} />)}
          </div>
        </Card>
      ))}
    </div>
  );
}

function WorkflowsPanel(): JSX.Element {
  const wf = useQuery<{ rows: Row[] }>({ queryKey: ["workflow-rules"], queryFn: () => api.get("/workflow-rules") });
  if (wf.isLoading) return <Loading />;
  const rows = wf.data?.rows ?? [];
  return (
    <Card>
      {rows.length === 0 ? <EmptyState title="Sin workflows" body="Las reglas de automatización aparecerán aquí." /> : (
        <table className="w-full">
          <thead><tr className="bg-canvas"><th className="th">Nombre</th><th className="th">Módulo</th><th className="th">Disparador</th><th className="th">Estado</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r["id"])}>
                <td className="td font-medium">{String(r["name"])}</td>
                <td className="td">{String(r["module"])}</td>
                <td className="td text-ink-secondary">{String(r["triggerType"])}</td>
                <td className="td">{r["isActive"] ? <Badge color="green">Activo</Badge> : <Badge>Inactivo</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function WebformsPanel(): JSX.Element {
  const wf = useQuery<{ rows: Row[] }>({ queryKey: ["webforms"], queryFn: () => api.get("/webforms") });
  if (wf.isLoading) return <Loading />;
  const rows = wf.data?.rows ?? [];
  return (
    <Card>
      {rows.length === 0 ? <EmptyState title="Sin formularios web" /> : (
        <table className="w-full">
          <thead><tr className="bg-canvas"><th className="th">Nombre</th><th className="th">Enlace público</th><th className="th">Envíos</th><th className="th">Estado</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r["id"])}>
                <td className="td font-medium">{String(r["name"])}</td>
                <td className="td"><a className="link" href={`/f/${String(r["publicToken"])}`} target="_blank" rel="noreferrer">/f/{String(r["publicToken"]).slice(0, 10)}…</a></td>
                <td className="td">{String(r["submissionsCount"] ?? 0)}</td>
                <td className="td">{r["isActive"] ? <Badge color="green">Activo</Badge> : <Badge>Inactivo</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function ImportsPanel(): JSX.Element {
  const jobs = useQuery<{ rows: Row[] }>({ queryKey: ["import-jobs"], queryFn: () => api.get("/import-jobs") });
  if (jobs.isLoading) return <Loading />;
  const rows = jobs.data?.rows ?? [];
  return (
    <Card>
      {rows.length === 0 ? <EmptyState title="Sin importaciones" body="El historial de importaciones aparecerá aquí." /> : (
        <table className="w-full">
          <thead><tr className="bg-canvas"><th className="th">Archivo</th><th className="th">Módulo</th><th className="th">Creados</th><th className="th">Estado</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r["id"])}>
                <td className="td">{String(r["fileName"])}</td>
                <td className="td">{String(r["module"])}</td>
                <td className="td">{String(r["created"] ?? 0)}</td>
                <td className="td">{String(r["status"])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ─────────────────────────── USUARIOS ───────────────────────────
interface UserRow { id: string; firstName: string; lastName: string; email: string; status: string; phone: string | null; cargo: string | null; profileId: string; profileName: string; roleId: string; roleName: string; allowCo?: boolean; allowPe?: boolean; }
interface OptionRow { id: string; name: string }

const STATUS_COLOR: Record<string, string> = { active: "green", invited: "amber", deactivated: "gray" };
const STATUS_LABEL: Record<string, string> = { active: "Activo", invited: "Invitado", deactivated: "Inactivo" };

export function UsersPanel(): JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState<{ mode: "new" } | { mode: "edit"; user: UserRow } | null>(null);
  const [q, setQ] = useState("");
  const [profileF, setProfileF] = useState("");
  const [roleF, setRoleF] = useState("");
  const [statusF, setStatusF] = useState("");
  const users = useQuery<{ rows: UserRow[] }>({ queryKey: ["users"], queryFn: () => api.get("/users") });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/users/${id}`, { status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Usuario actualizado."); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo actualizar."),
  });

  const allRows = users.data?.rows ?? [];
  const profileNames = [...new Set(allRows.map((u) => u.profileName))].sort();
  const roleNames = [...new Set(allRows.map((u) => u.roleName))].sort();
  const rows = allRows.filter((u) =>
    (!q.trim() || matchesQuery(`${u.firstName} ${u.lastName} ${u.email} ${u.cargo ?? ""}`, q))
    && (!profileF || u.profileName === profileF)
    && (!roleF || u.roleName === roleF)
    && (!statusF || u.status === statusF),
  );
  const done = (): void => { setForm(null); void qc.invalidateQueries({ queryKey: ["users"] }); };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-ink-secondary">
          {q || profileF || roleF || statusF ? <><b>{rows.length}</b> de {allRows.length} usuario(s)</> : <>{allRows.length} usuario(s) en la organización.</>}
        </p>
        {!form && <Button variant="primary" onClick={() => setForm({ mode: "new" })}>+ Nuevo usuario</Button>}
      </div>

      {!form && (
        <div className="flex items-center gap-2 flex-wrap">
          <SearchInput value={q} onValueChange={setQ} placeholder="Buscar por nombre, correo o cargo…" className="w-64" />
          <Select className="w-auto min-w-36" value={profileF} onChange={(e) => setProfileF(e.target.value)}>
            <option value="">Perfil: todos</option>
            {profileNames.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
          <Select className="w-auto min-w-36" value={roleF} onChange={(e) => setRoleF(e.target.value)}>
            <option value="">Rol: todos</option>
            {roleNames.map((r) => <option key={r} value={r}>{r}</option>)}
          </Select>
          <Select className="w-auto min-w-36" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
            <option value="">Estado: todos</option>
            <option value="active">Activo</option>
            <option value="invited">Invitado</option>
            <option value="deactivated">Inactivo</option>
          </Select>
          {(q || profileF || roleF || statusF) && (
            <button className="text-xs text-primary hover:underline" onClick={() => { setQ(""); setProfileF(""); setRoleF(""); setStatusF(""); }}>Limpiar</button>
          )}
        </div>
      )}

      {form
        ? <UserFormModal {...(form.mode === "edit" ? { user: form.user } : {})} onClose={() => setForm(null)} onSaved={done} />
        : (
        <Card>
          {users.isLoading ? <Loading /> : allRows.length === 0 ? <EmptyState title="Sin usuarios" /> : rows.length === 0 ? <EmptyState title="Sin resultados" body="Ningún usuario coincide con el filtro." /> : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full table-fixed">
                <thead><tr className="bg-canvas">
                  <th className="th">Nombre</th><th className="th">Correo</th><th className="th w-28">Perfil</th><th className="th w-36">Rol</th><th className="th w-16">Países</th><th className="th w-24">Estado</th><th className="th text-right w-20">Acciones</th>
                </tr></thead>
                <tbody>
                  {rows.map((u) => (
                    <tr key={u.id} className="hover:bg-surface-hover transition-colors">
                      <td className="td font-medium">
                        <div className="truncate">{u.firstName} {u.lastName}</div>
                        {u.cargo && <div className="text-xs text-ink-muted font-normal truncate">{u.cargo}</div>}
                      </td>
                      <td className="td text-ink-secondary"><div className="truncate" title={u.email}>{u.email}</div></td>
                      <td className="td"><div className="truncate" title={u.profileName}>{u.profileName}</div></td>
                      <td className="td"><div className="truncate" title={u.roleName}>{u.roleName}</div></td>
                      <td className="td">
                        <div className="flex items-center gap-1" title={[u.allowCo ? "Colombia" : null, u.allowPe ? "Perú" : null].filter(Boolean).join(" · ")}>
                          {u.allowCo && <CountryFlag country="CO" className="w-4 h-3" />}
                          {u.allowPe && <CountryFlag country="PE" className="w-4 h-3" />}
                        </div>
                      </td>
                      <td className="td"><Badge color={STATUS_COLOR[u.status] ?? "gray"}>{STATUS_LABEL[u.status] ?? u.status}</Badge></td>
                      <td className="td">
                        <div className="flex items-center gap-1 justify-end">
                          <button title="Editar" aria-label="Editar" onClick={() => setForm({ mode: "edit", user: u })}
                            className="p-1.5 rounded-md text-ink-secondary hover:bg-primary-soft hover:text-primary transition-colors">
                            <Pencil size={16} strokeWidth={1.75} />
                          </button>
                          {u.status === "deactivated"
                            ? <button title="Activar" aria-label="Activar" onClick={() => setStatus.mutate({ id: u.id, status: "active" })}
                                className="p-1.5 rounded-md text-ink-secondary hover:bg-success/10 hover:text-success transition-colors">
                                <UserCheck size={16} strokeWidth={1.75} />
                              </button>
                            : <button title="Desactivar" aria-label="Desactivar" onClick={() => { if (window.confirm(`¿Desactivar a ${u.firstName}? No podrá iniciar sesión.`)) setStatus.mutate({ id: u.id, status: "deactivated" }); }}
                                className="p-1.5 rounded-md text-ink-secondary hover:bg-danger/10 hover:text-danger transition-colors">
                                <UserX size={16} strokeWidth={1.75} />
                              </button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function UserFormModal({ user, onClose, onSaved }: { user?: UserRow; onClose: () => void; onSaved: () => void }): JSX.Element {
  const editing = Boolean(user);
  const profiles = useQuery<{ rows: OptionRow[] }>({ queryKey: ["profiles"], queryFn: () => api.get("/profiles") });
  const roles = useQuery<{ rows: OptionRow[] }>({ queryKey: ["roles"], queryFn: () => api.get("/roles") });

  const [firstName, setFirstName] = useState(user?.firstName ?? "");
  const [lastName, setLastName] = useState(user?.lastName ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [cargo, setCargo] = useState(user?.cargo ?? "");
  const [password, setPassword] = useState("");
  const [profileId, setProfileId] = useState(user?.profileId ?? "");
  const [roleId, setRoleId] = useState(user?.roleId ?? "");
  const [allowCo, setAllowCo] = useState(user?.allowCo ?? true);
  const [allowPe, setAllowPe] = useState(user?.allowPe ?? false);
  const [err, setErr] = useState<Record<string, string>>({});

  const profileOptions = profiles.data?.rows ?? [];
  const roleOptions = roles.data?.rows ?? [];
  // En creación: preseleccionar Comercial + Desarrollador de negocios. En edición: el actual.
  const defaultProfile = user?.profileId ?? profileOptions.find((p) => p.name === "Comercial")?.id ?? profileOptions[0]?.id ?? "";
  const defaultRole = user?.roleId ?? roleOptions.find((r) => r.name === "Desarrollador de negocios")?.id ?? roleOptions[0]?.id ?? "";
  const pId = profileId || defaultProfile;
  const rId = roleId || defaultRole;

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => editing ? api.patch(`/users/${user!.id}`, body) : api.post("/users", body),
    onSuccess: () => { toast.success(editing ? "Cambios guardados." : "Usuario creado."); onSaved(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });

  const submit = (): void => {
    const e: Record<string, string> = {};
    if (!firstName.trim()) e["firstName"] = "Este campo es obligatorio.";
    if (!lastName.trim()) e["lastName"] = "Este campo es obligatorio.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e["email"] = "Ingresa un correo válido.";
    if (!editing && password.length < 8) e["password"] = "Mínimo 8 caracteres.";
    if (editing && password && password.length < 8) e["password"] = "Mínimo 8 caracteres.";
    if (!pId) e["profileId"] = "Selecciona un perfil.";
    if (!rId) e["roleId"] = "Selecciona un rol.";
    if (!allowCo && !allowPe) e["countries"] = "Selecciona al menos un país.";
    setErr(e);
    if (Object.keys(e).length > 0) return;
    const body: Record<string, unknown> = { firstName, lastName, email, profileId: pId, roleId: rId, phone: phone || undefined, cargo: cargo || undefined, allowCo, allowPe };
    if (password) body["password"] = password;
    save.mutate(body);
  };

  return (
    <Modal open onClose={onClose} title={editing ? `Editar · ${user!.firstName} ${user!.lastName}` : "Nuevo usuario"} wide
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? "Guardando…" : (editing ? "Guardar cambios" : "Crear usuario")}</Button></>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Nombre" required {...(err["firstName"] ? { error: err["firstName"] } : {})}><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Field>
        <Field label="Apellido" required {...(err["lastName"] ? { error: err["lastName"] } : {})}><Input value={lastName} onChange={(e) => setLastName(e.target.value)} /></Field>
        <Field label="Correo" required {...(err["email"] ? { error: err["email"] } : {})}><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Teléfono / Móvil"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+57 3xx xxxxxxx" /></Field>
        <Field label="Cargo" hint="Aparece como firma en las cotizaciones que envíe este usuario."><Input value={cargo} onChange={(e) => setCargo(e.target.value)} placeholder="Ej: Gerente Comercial LATAM" /></Field>
        <Field label={editing ? "Nueva contraseña" : "Contraseña temporal"} required={!editing}
          hint={editing ? "Déjala vacía para no cambiarla." : "El usuario podrá cambiarla luego."}
          {...(err["password"] ? { error: err["password"] } : {})}>
          <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={editing ? "••••••••" : ""} />
        </Field>
        <Field label="Perfil (permisos)" required {...(err["profileId"] ? { error: err["profileId"] } : {})}>
          <Select value={pId} onChange={(e) => setProfileId(e.target.value)}>
            {profileOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="Rol (visibilidad)" required {...(err["roleId"] ? { error: err["roleId"] } : {})}>
          <Select value={rId} onChange={(e) => setRoleId(e.target.value)}>
            {roleOptions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
        </Field>
        <div className="sm:col-span-2">
          <Field label="Países autorizados" required {...(err["countries"] ? { error: err["countries"] } : {})}
            hint="El usuario solo verá y gestionará la información de los países marcados.">
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
                <input type="checkbox" className="rounded border-line" checked={allowCo} onChange={(e) => setAllowCo(e.target.checked)} />
                <CountryFlag country="CO" /> Colombia
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-secondary cursor-pointer">
                <input type="checkbox" className="rounded border-line" checked={allowPe} onChange={(e) => setAllowPe(e.target.checked)} />
                <CountryFlag country="PE" /> Perú
              </label>
            </div>
          </Field>
        </div>
      </div>
    </Modal>
  );
}

// ─────────────────────────── CAMPOS PERSONALIZADOS ───────────────────────────
const CF_MODULES: Array<[string, string]> = [["accounts", "Cuentas"], ["contacts", "Contactos"], ["leads", "Leads"], ["deals", "Negocios"], ["products", "Servicios"]];
const CF_TYPES: Array<[string, string]> = [["picklist", "Lista desplegable"], ["text", "Texto"], ["textarea", "Texto largo"], ["integer", "Número entero"], ["decimal", "Decimal"], ["currency", "Moneda"], ["percent", "Porcentaje"], ["date", "Fecha"], ["boolean", "Sí / No"]];
const CF_TYPE_LABEL: Record<string, string> = Object.fromEntries(CF_TYPES);

function CustomFieldsPanel(): JSX.Element {
  const qc = useQueryClient();
  const [module, setModule] = useState("accounts");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<CustomFieldRow | null>(null);
  const fields = useAllCustomFields(module);
  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["custom-fields-all", module] });
    void qc.invalidateQueries({ queryKey: ["custom-fields", module] });
  };
  // Solo desplegables (los que tienen opciones editables).
  const rows = ((fields.data?.rows ?? []) as CustomFieldRow[]).filter((f) => f.fieldType === "picklist");

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-secondary">Campos de</span>
          <Select value={module} onChange={(e) => setModule(e.target.value)} className="w-40">
            {CF_MODULES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </div>
        {!creating && !editing && <Button variant="primary" onClick={() => setCreating(true)}>+ Nuevo campo</Button>}
      </div>

      {creating ? (
        <NewCustomFieldModal module={module} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); invalidate(); }} />
      ) : editing ? (
        <OptionsEditor field={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); invalidate(); }} />
      ) : (
        <Card>
          {fields.isLoading ? <Loading /> : rows.length === 0 ? (
            <EmptyState title="Sin campos personalizados" body="Crea uno para capturar datos propios en este módulo." />
          ) : (
            <table className="w-full">
              <thead><tr className="bg-canvas">
                <th className="th">Etiqueta</th><th className="th">Tipo</th><th className="th">Opciones</th><th className="th">Estado</th><th className="th"></th>
              </tr></thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td className="td font-medium">{f.label}<span className="text-ink-muted font-normal"> · {f.apiName}</span></td>
                    <td className="td">{CF_TYPE_LABEL[f.fieldType] ?? f.fieldType}</td>
                    <td className="td text-ink-secondary">{f.fieldType === "picklist" ? `${(f.options ?? []).length} opciones` : "—"}</td>
                    <td className="td">{f.isActive ? <Badge color="green">Activo</Badge> : <Badge>Inactivo</Badge>}</td>
                    <td className="td text-right whitespace-nowrap">
                      {f.fieldType === "picklist" && <Button small variant="primary" onClick={() => setEditing(f)}>Opciones</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}

function NewCustomFieldModal({ module, onClose, onCreated }: { module: string; onClose: () => void; onCreated: () => void }): JSX.Element {
  const [label, setLabel] = useState("");
  const [fieldType, setFieldType] = useState("picklist");
  const [section, setSection] = useState("");
  const [optionsText, setOptionsText] = useState("");
  const [err, setErr] = useState<Record<string, string>>({});
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post("/custom-fields", body),
    onSuccess: () => { toast.success("Campo creado."); onCreated(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo crear el campo."),
  });
  const submit = (): void => {
    const e: Record<string, string> = {};
    if (!label.trim()) e["label"] = "Este campo es obligatorio.";
    const options = optionsText.split("\n").map((o) => o.trim()).filter(Boolean);
    if (fieldType === "picklist" && options.length === 0) e["options"] = "Agrega al menos una opción.";
    setErr(e);
    if (Object.keys(e).length > 0) return;
    const body: Record<string, unknown> = { module, label: label.trim(), fieldType };
    if (section.trim()) body["section"] = section.trim();
    if (fieldType === "picklist") body["options"] = options;
    create.mutate(body);
  };
  return (
    <Modal open onClose={onClose} title="Nuevo campo personalizado" wide
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" onClick={submit} disabled={create.isPending}>Crear campo</Button></>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Etiqueta" required {...(err["label"] ? { error: err["label"] } : {})}><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ej. Macrosector" /></Field>
        <Field label="Tipo" required>
          <Select value={fieldType} onChange={(e) => setFieldType(e.target.value)}>
            {CF_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </Field>
        <Field label="Sección (opcional)" hint="Agrupa el campo en la ficha."><Input value={section} onChange={(e) => setSection(e.target.value)} placeholder="Ej. Clasificación" /></Field>
        {fieldType === "picklist" && (
          <div className="sm:col-span-2">
            <Field label="Opciones (una por línea)" required {...(err["options"] ? { error: err["options"] } : {})}>
              <Textarea rows={5} value={optionsText} onChange={(e) => setOptionsText(e.target.value)} placeholder={"COMERCIO\nMANUFACTURA\nSERVICIOS"} />
            </Field>
          </div>
        )}
      </div>
    </Modal>
  );
}

function OptionsEditor({ field, onClose, onSaved }: { field: CustomFieldRow; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [opts, setOpts] = useState<string[]>(field.options ?? []);
  const [newOpt, setNewOpt] = useState("");
  const [q, setQ] = useState("");
  const save = useMutation({
    mutationFn: () => api.patch(`/custom-fields/${field.id}`, { options: opts }),
    onSuccess: () => { toast.success("Opciones guardadas."); onSaved(); },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "No se pudo guardar."),
  });
  const add = (): void => {
    const v = newOpt.trim();
    if (v && !opts.some((o) => o.toLowerCase() === v.toLowerCase())) setOpts([v, ...opts]);
    setNewOpt("");
  };
  const remove = (o: string): void => setOpts(opts.filter((x) => x !== o));
  const needle = q.trim().toLowerCase();
  const filtered = (needle ? opts.filter((o) => o.toLowerCase().includes(needle)) : opts).slice(0, 300);
  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-ink">Opciones de «{field.label}»</h3>
        <div className="flex gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? "Guardando…" : "Guardar opciones"}</Button>
        </div>
      </div>
      <div className="flex gap-2">
        <Input value={newOpt} onChange={(e) => setNewOpt(e.target.value)} placeholder="Nueva opción…"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="max-w-md" />
        <Button onClick={add}>+ Agregar</Button>
      </div>
      {opts.length > 15 && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar opciones…" className="max-w-md" />}
      <div className="text-xs text-ink-muted">{opts.length} opciones{needle ? ` · mostrando ${filtered.length}` : ""}</div>
      <div className="border border-line rounded-lg divide-y divide-line max-h-96 overflow-y-auto">
        {filtered.length === 0 ? <div className="px-3 py-3 text-sm text-ink-muted">Sin opciones.</div> : filtered.map((o) => (
          <div key={o} className="flex items-center justify-between px-3 py-2 text-sm">
            <span>{o}</span>
            <button type="button" className="text-red-500 hover:text-red-700 text-xs" onClick={() => remove(o)}>Quitar ✕</button>
          </div>
        ))}
      </div>
      <p className="text-xs text-ink-muted">Los cambios se aplican al pulsar «Guardar opciones».</p>
    </Card>
  );
}
